import Analytics from '@/utils/analytics';
import ChevronDownRoundedIcon from '@assets/icons/chevron-down-rounded.svg';
import EmployeesIcon from '@assets/icons/employees.svg';
import i18n from '@/utils/i18n';
import LockIcon from '@assets/icons/lock.svg';
import React, { useCallback, useMemo, useState } from 'react';
import { ActionSheetKeyboardAwareView } from '@/components/order-guide/action-sheet-keyboard-aware-view';
import {
  BORDER,
  BORDER_RADIUS_SMALL,
  FONTS,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import {
  Cart,
  CartVisibility,
  isReservedCartName,
  MAX_CART_NAME_LENGTH,
  useCartActions,
} from './hooks/use-cart-actions';
import {
  CombinedActionSheet,
  JumpToFn,
} from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { FulfillmentActionSheetFulfillmentDaysView } from '@/components/partners/fulfillment-action-sheet-fulfillment-days-view';
import { FulfillmentActionSheetOptionsView } from '@/components/partners/fulfillment-action-sheet-options-view';
import {
  FulfillmentActionSheetRoute,
  useFulfillmentPicker,
} from '@/components/partners/use-fulfillment-picker';
import { FulfillmentActionSheetTimeslotView } from '@/components/partners/fulfillment-action-sheet-timeslot-view';
import { FulfillmentName, NewOrder_OrderFieldsFragment } from '@/types';
import { FulfillmentPickerPopover } from '@/components/partners/fulfillment-picker-popover';
import {
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { SelectedFulfillmentType } from '@/contexts/new-order-actions-context-types';
import { SelectInput } from '@/components/select-input';
import { SvgProps } from 'react-native-svg';
import { TextInput } from '@/components/text-input';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';

enum CartFormRoute {
  Form = 'FORM',
}

interface CartFormActionSheetProps {
  editingCart: Cart | null;
  onCancel: () => void;
  onSaved: (savedFulfillment: SelectedFulfillmentType | null) => void;
}

const findCartOrder = (
  draftOrders: NewOrder_OrderFieldsFragment[],
  editingCart: Cart | null,
) => draftOrders.find((order) => order.uuid === editingCart?.orderUUID) ?? null;

const getCartFulfillment = (
  order: NewOrder_OrderFieldsFragment | null,
): SelectedFulfillmentType | null =>
  order?.fulfillment_type && order.restaurant_desired_delivery_time
    ? {
        date: order.restaurant_desired_delivery_time,
        fulfillmentSettingsUUID: null,
        timeslot:
          order.fulfillment_timeslot_start && order.fulfillment_timeslot_end
            ? {
                endTime: order.fulfillment_timeslot_end,
                startTime: order.fulfillment_timeslot_start,
              }
            : null,
        type: order.fulfillment_type as FulfillmentName,
      }
    : null;

export const CartFormActionSheet: React.FC<CartFormActionSheetProps> = ({
  editingCart,
  onCancel,
  onSaved,
}) => {
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const { height } = useWindowDimensions();
  const { createCart, editCart } = useCartActions();
  const { draftOrders, selectableFulfillmentOptions } = useNewOrderContext();
  const { nextAvailableFulfillment } = useNewOrderActionsContext();
  const [name, setName] = useState(editingCart?.name ?? '');
  const [visibility, setVisibility] = useState<CartVisibility>(
    editingCart?.visibility ?? 'PUBLIC',
  );
  const [isSaving, setIsSaving] = useState(false);
  const [activePickerRoute, setActivePickerRoute] =
    useState<FulfillmentActionSheetRoute | null>(null);
  const [fulfillment, setFulfillment] =
    useState<SelectedFulfillmentType | null>(() =>
      getCartFulfillment(findCartOrder(draftOrders, editingCart)),
    );

  const editingOrder = useMemo(
    () => findCartOrder(draftOrders, editingCart),
    [draftOrders, editingCart],
  );
  const isEdit = editingCart !== null;
  const shownFulfillment = fulfillment ?? nextAvailableFulfillment ?? null;
  const showPickerPopover = Platform.OS === 'web' && !isSmallLayout;

  const nameError = isReservedCartName(name)
    ? i18n.t('multi_cart.reserved_name_error')
    : null;
  const canSave = name.trim().length > 0 && !nameError;

  const handleFulfillmentSelected = useCallback(
    async (nextFulfillment: SelectedFulfillmentType) => {
      setFulfillment(nextFulfillment);
    },
    [],
  );

  const picker = useFulfillmentPicker({
    draftOrder: editingOrder,
    fulfillmentOptions: selectableFulfillmentOptions,
    initialSelectedFulfillment: shownFulfillment,
    onClose: onCancel,
    onSave: handleFulfillmentSelected,
    placeOrderShortcutEnabled: false,
  });
  const {
    clearTimeslot,
    defaultInitialRoute,
    listData,
    resetSelection,
    saveFulfillment,
    saveFulfillmentForToday,
    savingFulfillment,
    selectedFulfillmentDate,
    selectedFulfillmentLabel,
    selectedFulfillmentOption,
    selectedTimeslot,
    setSelectedFulfillmentDate,
    setSelectedFulfillmentOption,
    setSelectedTimeslot,
    setTimeslotOptionsForDay,
    timeslotOptionsForDay,
    timeslotRouteTitle,
  } = picker;

  const onSave = async () => {
    if (!canSave) {
      return;
    }
    setIsSaving(true);
    try {
      const saved =
        isEdit && editingCart?.orderUUID
          ? await editCart(editingCart.orderUUID, {
              fulfillment: shownFulfillment,
              name,
              visibility,
            })
          : await createCart({
              fulfillment: shownFulfillment,
              name,
              visibility,
            });
      if (saved) {
        onSaved(shownFulfillment);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const title = isEdit
    ? i18n.t('multi_cart.cart_settings')
    : i18n.t('multi_cart.new_cart');

  const pickerContentStyle = {
    maxHeight: Math.floor(height * 0.9),
    paddingHorizontal: 0,
  };

  const renderFulfillmentField = (
    jumpTo: JumpToFn<CartFormRoute | FulfillmentActionSheetRoute>,
  ) => {
    const field = (
      <SelectInput
        containerStyle={styles.field}
        inputContainerStyle={styles.select}
        label={i18n.t('multi_cart.fulfillment_date')}
        labelStyle={[TYPOGRAPHY.smallLabel, styles.label]}
        onPress={() => {
          if (!shownFulfillment) {
            return;
          }
          Analytics.track('multi_cart.cart_form.fulfillment_button');
          if (showPickerPopover) {
            resetSelection();
            setActivePickerRoute(defaultInitialRoute);
            return;
          }
          jumpTo(defaultInitialRoute);
        }}
        rightElement={
          <ChevronDownRoundedIcon
            height={14}
            stroke={theme.sk.color.icon.tertiary}
            width={14}
          />
        }
        testID="cart-form-fulfillment-select"
        useOuterLabel
        value={selectedFulfillmentLabel}
      />
    );

    if (!showPickerPopover) {
      return field;
    }

    return (
      <FulfillmentPickerPopover
        activeRoute={activePickerRoute}
        draftOrder={editingOrder}
        jumpTo={setActivePickerRoute}
        onClose={() => setActivePickerRoute(null)}
        picker={picker}
        trackingProperties={{ source: 'multi_cart_cart_form' }}
        trigger={field}
      />
    );
  };

  const renderPickerScene = (
    jumpTo: JumpToFn<CartFormRoute | FulfillmentActionSheetRoute>,
    routeKey: CartFormRoute | FulfillmentActionSheetRoute,
  ) => {
    const returnToForm = () => jumpTo(CartFormRoute.Form);

    switch (routeKey) {
      case FulfillmentActionSheetRoute.Base:
        return (
          <FulfillmentActionSheetOptionsView
            isLoading={false}
            listData={listData}
            onSelect={(option) => {
              const { fulfillmentOption, nextRouteKey: nextSelectedView } =
                option.metadata!;

              if (nextSelectedView) {
                setSelectedFulfillmentOption(fulfillmentOption);
                jumpTo(nextSelectedView);
              } else {
                saveFulfillmentForToday(fulfillmentOption, returnToForm);
              }
            }}
            savingFulfillment={savingFulfillment}
          />
        );
      case FulfillmentActionSheetRoute.DaysView:
        if (!selectedFulfillmentOption) {
          return null;
        }
        return (
          <FulfillmentActionSheetFulfillmentDaysView
            clearTimeslot={clearTimeslot}
            draftOrder={editingOrder}
            fulfillmentOption={selectedFulfillmentOption}
            initialFulfillmentDate={selectedFulfillmentDate}
            isSaving={Boolean(savingFulfillment)}
            jumpToTimeslots={(timeslotOptions) => {
              setTimeslotOptionsForDay(timeslotOptions);
              jumpTo(FulfillmentActionSheetRoute.TimeslotSelect);
            }}
            onDayChange={setSelectedFulfillmentDate}
            onSave={(dateMoment, timeslot, resolvedFulfillmentOption) => {
              saveFulfillment(
                {
                  date: dateMoment.toISOString(),
                  fulfillmentSettingsUUID:
                    resolvedFulfillmentOption.fulfillment_settings_id,
                  timeslot,
                  type: resolvedFulfillmentOption.fulfillment_name,
                },
                returnToForm,
              );
            }}
            selectedTimeslot={selectedTimeslot}
            trackingProperties={{ source: 'multi_cart_cart_form' }}
          />
        );
      case FulfillmentActionSheetRoute.TimeslotSelect:
        if (!timeslotOptionsForDay || !selectedFulfillmentOption) {
          return null;
        }
        return (
          <FulfillmentActionSheetTimeslotView
            onSelect={(timeslot) => {
              setSelectedTimeslot(timeslot);
              jumpTo(FulfillmentActionSheetRoute.DaysView);
            }}
            timeslotOptions={timeslotOptionsForDay}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CombinedActionSheet
      animateTransitions
      hasStickyHeader={false}
      initialRoute={CartFormRoute.Form}
      isFocusableForKeyboardShortcuts
      onClose={onCancel}
      onRouteChange={(nextKey) => {
        if (nextKey === FulfillmentActionSheetRoute.Base) {
          resetSelection();
        }
      }}
      renderScene={({ jumpTo, routeKey }) =>
        routeKey !== CartFormRoute.Form ? (
          renderPickerScene(jumpTo, routeKey)
        ) : (
          <ActionSheetKeyboardAwareView
            style={[
              styles.content,
              isSmallLayout ? null : styles.contentLargeLayout,
            ]}
          >
            <Text style={TYPOGRAPHY.h3Bold}>{title}</Text>
            <Text style={[TYPOGRAPHY.h4, styles.subtitle]}>
              {isEdit
                ? i18n.t('multi_cart.edit_subtitle')
                : i18n.t('multi_cart.new_subtitle')}
            </Text>

            <TextInput
              containerStyle={[styles.field, styles.nameInput]}
              error={nameError}
              label={i18n.t('multi_cart.cart_name')}
              labelStyle={[TYPOGRAPHY.smallLabel, styles.label]}
              maxLength={MAX_CART_NAME_LENGTH}
              onChangeText={setName}
              placeholder={i18n.t('multi_cart.cart_name_placeholder')}
              useOuterLabel
              value={name}
            />

            {renderFulfillmentField(jumpTo)}

            <Text style={[TYPOGRAPHY.smallLabel, styles.label]}>
              {i18n.t('multi_cart.visibility')}
            </Text>
            <View style={styles.visRow}>
              <VisibilityOption
                active={visibility === 'PUBLIC'}
                caption={i18n.t('multi_cart.public_caption')}
                Icon={EmployeesIcon}
                onPress={() => setVisibility('PUBLIC')}
                title={i18n.t('multi_cart.public')}
              />
              <VisibilityOption
                active={visibility === 'PRIVATE'}
                caption={i18n.t('multi_cart.private_caption')}
                Icon={LockIcon}
                onPress={() => setVisibility('PRIVATE')}
                title={i18n.t('multi_cart.private')}
              />
            </View>

            <View style={styles.actions}>
              <Button
                containerStyle={styles.actionButton}
                disabled={isSaving}
                onPress={onCancel}
                trackingData={{ name: 'multi_cart.cart_form.cancel' }}
                type="tertiary"
              >
                {i18n.t('cancel')}
              </Button>
              <Button
                containerStyle={styles.actionButton}
                disabled={!canSave || isSaving}
                loading={isSaving}
                onPress={onSave}
                trackingData={{ name: 'multi_cart.cart_form.save' }}
                type="primary"
              >
                {isEdit
                  ? i18n.t('multi_cart.save_changes')
                  : i18n.t('multi_cart.create_cart')}
              </Button>
            </View>
          </ActionSheetKeyboardAwareView>
        )
      }
      routes={{
        [CartFormRoute.Form]: {
          modalHeaderStyle: styles.collapsedHeader,
          previousKey: null,
          showCloseButton: false,
        },
        [FulfillmentActionSheetRoute.Base]: {
          contentStyle: pickerContentStyle,
          dismissKey: CartFormRoute.Form,
          previousKey: CartFormRoute.Form,
          showCloseButton: false,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('fulfillment_select'),
        },
        [FulfillmentActionSheetRoute.DaysView]: {
          contentStyle: [pickerContentStyle, styles.pickerDaysContent],
          dismissKey: CartFormRoute.Form,
          previousKey:
            selectableFulfillmentOptions.length > 1
              ? FulfillmentActionSheetRoute.Base
              : CartFormRoute.Form,
          showCloseButton: false,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('fulfillment_date_select'),
        },
        [FulfillmentActionSheetRoute.TimeslotSelect]: {
          contentStyle: pickerContentStyle,
          dismissKey: CartFormRoute.Form,
          previousKey: FulfillmentActionSheetRoute.DaysView,
          showCloseButton: false,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: timeslotRouteTitle,
        },
      }}
      titleStyle={styles.pickerTitle}
    />
  );
};

interface VisibilityOptionProps {
  active: boolean;
  caption: string;
  Icon: React.FC<SvgProps>;
  onPress: () => void;
  title: string;
}

const VisibilityOption: React.FC<VisibilityOptionProps> = ({
  active,
  title,
  caption,
  Icon,
  onPress,
}) => {
  const [styles, theme] = useStyles();
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.visOption,
        active ? styles.visOptionActive : null,
        pressed ? styles.pressed : null,
      ]}
    >
      <View style={styles.visTitleRow}>
        <Icon height={15} stroke={theme.sk.color.icon.primary} width={15} />
        <Text style={[TYPOGRAPHY.h4SemiBold, styles.visTitle]}>{title}</Text>
      </View>
      <Text style={[TYPOGRAPHY.smallLabel, styles.visCaption]}>{caption}</Text>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: { flex: 1 },
  actions: { flexDirection: 'row', gap: 16, marginTop: 22 },
  collapsedHeader: { padding: 0 },
  content: { paddingBottom: 4 },
  contentLargeLayout: { paddingTop: 22 },
  field: { marginBottom: 14 },
  label: {
    color: theme.colors.secondaryText,
    marginBottom: 6,
  },
  nameInput: BORDER.primary,
  pickerDaysContent: { paddingVertical: 16 },
  pickerTitle: { marginLeft: SCREEN_PADDING },
  pressed: { opacity: 0.5 },
  select: BORDER.primary,
  subtitle: {
    color: theme.colors.inactiveText,
    marginBottom: 18,
    marginTop: 3,
  },
  visCaption: {
    color: theme.colors.inactiveText,
    marginTop: 2,
  },
  visOption: {
    borderColor: theme.colors.stroke,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1.5,
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 11,
  },
  visOptionActive: {
    backgroundColor: theme.colors.accentLight,
    borderColor: theme.colors.accent,
  },
  visRow: { flexDirection: 'row', gap: 10 },
  visTitle: { ...FONTS.primaryBold },
  visTitleRow: { alignItems: 'center', flexDirection: 'row', gap: 7 },
}));
